/**
 * Fictional-but-convincing club + match generator. Pure, deterministic from an Rng.
 */
import type { MatchConfig, PlayerProfile, Rng, TeamProfile, TeamSide, TimeOfDay, Weather } from '../core/contracts';
import { createRng } from '../core/rng';
import { FORMATIONS } from './formations';

// ---------------------------------------------------------------------------
// Name data
// ---------------------------------------------------------------------------

interface Nation { name: string; first: readonly string[]; last: readonly string[] }

const NATIONS: Record<string, Nation> = {
  ENG: { name: 'England', first: ['Jack', 'Harry', 'Callum', 'Josh', 'Liam', 'Tom', 'Ben', 'Ryan', 'Connor', 'Jordan', 'Kieran', 'Marcus', 'Danny', 'Reece', 'Jamie', 'Ollie', 'Lewis', 'Sam', 'Aaron', 'Kyle'],
    last: ['Whitlock', 'Ashworth', 'Barnes', 'Holloway', 'Pritchard', 'Fenwick', 'Crowther', 'Mabbutt', 'Thornton', 'Hargreaves', 'Bellamy', 'Cartwright', 'Dawson', 'Elliott', 'Featherstone', 'Gilchrist', 'Haworth', 'Lockwood', 'Marsden', 'Oakley', 'Pickering', 'Radcliffe', 'Sutcliffe', 'Tennant', 'Wainwright', 'Yardley', 'Kettering', 'Brampton', 'Colley', 'Stokes'] },
  SCO: { name: 'Scotland', first: ['Callum', 'Fraser', 'Euan', 'Ross', 'Scott', 'Lewis', 'Craig', 'Gregor', 'Ally', 'Ruaridh'],
    last: ['McAllister', 'Fergusson', 'Drummond', 'MacLeod', 'Kerr', 'Buchanan', 'McGinley', 'Sinclair', 'Lennox', 'Murdoch', 'Baird', 'Crichton'] },
  IRL: { name: 'Ireland', first: ['Seán', 'Cian', 'Darragh', 'Eoin', 'Conor', 'Niall', 'Ciarán', 'Shane', 'Rory'],
    last: ["O'Donnell", 'Brennan', 'Kavanagh', 'Gallagher', 'Doherty', 'Mulligan', 'Keane', "O'Sullivan", 'Duffy', 'Fitzgerald'] },
  WAL: { name: 'Wales', first: ['Rhys', 'Gethin', 'Owain', 'Dylan', 'Iwan', 'Gareth', 'Aled'],
    last: ['Pryce', 'Llewellyn', 'Vaughan', 'Gwynne', 'Hopkins', 'Bevan', 'Pugh', 'Morgan'] },
  ESP: { name: 'Spain', first: ['Pablo', 'Álvaro', 'Iker', 'Sergio', 'Javi', 'Marcos', 'Rubén', 'Dani', 'Adrián', 'Hugo'],
    last: ['Herrera', 'Castillo', 'Navarro', 'Ortega', 'Delgado', 'Morales', 'Iglesias', 'Romero', 'Vidal', 'Serrano', 'Cabrera'] },
  POR: { name: 'Portugal', first: ['João', 'Rúben', 'Tiago', 'Diogo', 'Nuno', 'Rafael', 'Gonçalo'],
    last: ['Almeida', 'Carvalho', 'Pereira', 'Monteiro', 'Teixeira', 'Fonseca', 'Coelho', 'Magalhães'] },
  BRA: { name: 'Brazil', first: ['Thiago', 'Lucas', 'Gabriel', 'Matheus', 'Vinícius', 'Rodrigo', 'Caio', 'Wesley', 'Felipe'],
    last: ['Ribeiro', 'Santos', 'Moura', 'Barbosa', 'Nascimento', 'Cardoso', 'Andrade', 'Rocha', 'Batista'] },
  FRA: { name: 'France', first: ['Théo', 'Lucas', 'Hugo', 'Antoine', 'Mathis', 'Kylian', 'Rayan', 'Bastien', 'Yanis', 'Corentin'],
    last: ['Lefèvre', 'Moreau', 'Girard', 'Fontaine', 'Rousseau', 'Mercier', 'Chevalier', 'Lambert', 'Garnier', 'Dubois'] },
  GER: { name: 'Germany', first: ['Lukas', 'Jonas', 'Leon', 'Felix', 'Niklas', 'Tim', 'Moritz', 'Julian'],
    last: ['Hoffmann', 'Brandt', 'Krüger', 'Wagner', 'Schäfer', 'Neumann', 'Vogel', 'Zimmermann', 'Hartmann'] },
  NED: { name: 'Netherlands', first: ['Daan', 'Sem', 'Bram', 'Jesper', 'Ruben', 'Thijs', 'Luuk'],
    last: ['de Vries', 'van Dam', 'Bakker', 'Visser', 'Jansen', 'de Groot', 'Smit', 'van Leeuwen'] },
  ITA: { name: 'Italy', first: ['Lorenzo', 'Matteo', 'Alessandro', 'Federico', 'Nicolò', 'Davide', 'Riccardo'],
    last: ['Rossi', 'Bianchi', 'Esposito', 'Ferrari', 'Colombo', 'Marchetti', 'Bellini', 'Conti'] },
  NGA: { name: 'Nigeria', first: ['Chidi', 'Emeka', 'Tunde', 'Victor', 'Samuel', 'Ademola', 'Kelechi', 'Ifeanyi'],
    last: ['Okafor', 'Adeyemi', 'Nwosu', 'Eze', 'Balogun', 'Okonkwo', 'Iheanacho', 'Ogunleye'] },
  GHA: { name: 'Ghana', first: ['Kwame', 'Kofi', 'Yaw', 'Kwabena', 'Emmanuel', 'Abdul'],
    last: ['Mensah', 'Asante', 'Boateng', 'Owusu', 'Appiah', 'Agyemang'] },
  SEN: { name: 'Senegal', first: ['Moussa', 'Cheikh', 'Ismaïla', 'Pape', 'Idrissa', 'Mamadou'],
    last: ['Diallo', 'Ndiaye', 'Sarr', 'Faye', 'Diouf', 'Cissé'] },
  CIV: { name: 'Ivory Coast', first: ['Serge', 'Wilfried', 'Yao', 'Franck', 'Jean-Philippe'],
    last: ['Koné', 'Bamba', 'Traoré', 'Kouassi', 'Gbamin'] },
  JPN: { name: 'Japan', first: ['Takumi', 'Daichi', 'Kaoru', 'Ritsu', 'Hiroki', 'Wataru'],
    last: ['Tanaka', 'Endo', 'Minamino', 'Kubo', 'Itakura', 'Morita'] },
  KOR: { name: 'South Korea', first: ['Min-jae', 'Heung-min', 'Kang-in', 'Hee-chan', 'Jae-sung'],
    last: ['Park', 'Kim', 'Lee', 'Hwang', 'Jeong'] },
  POL: { name: 'Poland', first: ['Jakub', 'Piotr', 'Kamil', 'Mateusz', 'Krzysztof'],
    last: ['Kowalczyk', 'Zieliński', 'Nowak', 'Wójcik', 'Lewandowicz'] },
  CRO: { name: 'Croatia', first: ['Luka', 'Ivan', 'Mateo', 'Josip', 'Ante'],
    last: ['Perišić', 'Kovačić', 'Brozović', 'Jurić', 'Vlašić'] },
  SWE: { name: 'Sweden', first: ['Emil', 'Viktor', 'Oscar', 'Gustav', 'Albin'],
    last: ['Lindqvist', 'Bergström', 'Forsberg', 'Holmberg', 'Ekdal'] },
  NOR: { name: 'Norway', first: ['Erling', 'Sander', 'Magnus', 'Kristoffer', 'Jørgen'],
    last: ['Haugland', 'Strand', 'Berge', 'Østigård', 'Solbakken'] },
  DEN: { name: 'Denmark', first: ['Mikkel', 'Rasmus', 'Kasper', 'Andreas', 'Jonas'],
    last: ['Højbjerg', 'Nørgaard', 'Christensen', 'Dolberg', 'Skov'] },
  ARG: { name: 'Argentina', first: ['Facundo', 'Lautaro', 'Nicolás', 'Exequiel', 'Enzo', 'Gonzalo'],
    last: ['Martínez', 'Acuña', 'Paredes', 'Palacios', 'Otamendi', 'Correa'] },
  COL: { name: 'Colombia', first: ['Juan', 'Luis', 'Jhon', 'Yerry', 'Davinson'],
    last: ['Cuadrado', 'Arias', 'Mojica', 'Lerma', 'Borja'] },
  MEX: { name: 'Mexico', first: ['Hirving', 'Edson', 'Raúl', 'Héctor', 'Jesús'],
    last: ['Lozano', 'Álvarez', 'Jiménez', 'Guardado', 'Corona'] },
  USA: { name: 'USA', first: ['Tyler', 'Weston', 'Brenden', 'Gio', 'Chris', 'DeAndre'],
    last: ['Adams', 'McKennie', 'Aaronson', 'Robinson', 'Pulisic', 'Turner'] },
  MAR: { name: 'Morocco', first: ['Achraf', 'Hakim', 'Sofyan', 'Youssef', 'Azzedine'],
    last: ['Amrabat', 'Ziyech', 'Boufal', 'En-Nesyri', 'Ounahi'] },
  TUR: { name: 'Türkiye', first: ['Arda', 'Hakan', 'Kerem', 'Ferdi', 'Orkun'],
    last: ['Çalhanoğlu', 'Aktürkoğlu', 'Kökçü', 'Kadıoğlu', 'Yıldız'] },
  BEL: { name: 'Belgium', first: ['Youri', 'Leandro', 'Arthur', 'Charles', 'Amadou'],
    last: ['Tielemans', 'Vertonghen', 'Theate', 'De Ketelaere', 'Onana'] },
};

/** Home nations a club might be "from" and how its squad is weighted. */
const DOMESTIC_MIX: { key: string; w: number }[] = [
  { key: 'ENG', w: 46 }, { key: 'SCO', w: 6 }, { key: 'IRL', w: 5 }, { key: 'WAL', w: 4 },
  { key: 'ESP', w: 3 }, { key: 'POR', w: 3 }, { key: 'BRA', w: 3 }, { key: 'FRA', w: 4 }, { key: 'GER', w: 2 },
  { key: 'NED', w: 2 }, { key: 'ITA', w: 2 }, { key: 'NGA', w: 3 }, { key: 'GHA', w: 2 }, { key: 'SEN', w: 2 },
  { key: 'CIV', w: 1.5 }, { key: 'JPN', w: 1.5 }, { key: 'KOR', w: 1 }, { key: 'POL', w: 1 }, { key: 'CRO', w: 1 },
  { key: 'SWE', w: 1 }, { key: 'NOR', w: 1 }, { key: 'DEN', w: 1 }, { key: 'ARG', w: 1.5 }, { key: 'COL', w: 1 },
  { key: 'MEX', w: 1 }, { key: 'USA', w: 1 }, { key: 'MAR', w: 1.5 }, { key: 'TUR', w: 1 }, { key: 'BEL', w: 1.5 },
];

const TOWNS = [
  'Northbridge', 'Castlemoor', 'Ashford Vale', 'Kingsbury', 'Redhaven', 'Westmarch', 'Elmstead', 'Harrowgate',
  'Saltburn', 'Thornbury', 'Oakfield', 'Brackenford', 'Ironvale', 'Merriton', 'Port Alder', 'Stanwick',
  'Hollowmere', 'Fernley', 'Whitcombe', 'Ravensgate', 'Duncliffe', 'Greywater', 'Halston', 'Calderbrook',
  'Marlow Heath', 'Eastwick', 'Brightling', 'Corran Bay', 'Ludgate', 'Penhallow', 'Wyvern Hill', 'Ambleside Port',
  'Steelhaven', 'Kestrel Moor', 'Blackwater', 'Silverdale', 'Coldharbour', 'Rookwood', 'Glenmorrow', 'Highcliffe',
];
const SUFFIXES = ['Rovers', 'Athletic', 'United', 'City', 'Town', 'Wanderers', 'Albion', 'FC', 'County', 'Rangers',
  'Harriers', 'Orient', 'Borough', 'Villa', 'Argyle', 'Celtic', 'Forest', 'Rovers', 'United', 'Athletic'];
const PREFIX_STYLES = ['Sporting', 'Real', 'AFC', 'Dynamo', 'Inter'];
const GROUNDS = ['Park', 'Road', 'Lane', 'Stadium', 'Ground', 'Arena', 'Meadow', 'Fields'];
const GROUND_NAMES = ['The Ironworks', 'The Old Foundry', 'The Dell', 'Riverside', 'The Kennel', 'Mill Lane', 'The Citadel',
  'The Hollow', 'Quayside Arena', 'The Furnace', 'Beacon Park', 'Victoria Ground', 'The Crucible', 'Copper Lane'];

interface Kit { primary: string; secondary: string }
const KITS: Kit[] = [
  { primary: '#c8102e', secondary: '#ffffff' }, // red / white
  { primary: '#0b3d91', secondary: '#ffffff' }, // royal blue / white
  { primary: '#6cabdd', secondary: '#1c2c5b' }, // sky / navy
  { primary: '#ffcd00', secondary: '#111111' }, // yellow / black
  { primary: '#1b5e20', secondary: '#ffffff' }, // bottle green / white
  { primary: '#7a003c', secondary: '#9fd3ff' }, // claret / sky
  { primary: '#f47a20', secondary: '#111111' }, // tangerine / black
  { primary: '#111111', secondary: '#ffffff' }, // black / white
  { primary: '#ffffff', secondary: '#0b1f4b' }, // white / navy
  { primary: '#4b0f6b', secondary: '#f5c400' }, // purple / gold
  { primary: '#00897b', secondary: '#ffffff' }, // teal / white
  { primary: '#8b0000', secondary: '#f2c14e' }, // maroon / amber
  { primary: '#1e88e5', secondary: '#ffeb3b' }, // azure / yellow
  { primary: '#e53935', secondary: '#111111' }, // scarlet / black
  { primary: '#0d1b2a', secondary: '#e0b84a' }, // midnight / gold
  { primary: '#2e7d32', secondary: '#ffd54f' }, // green / gold
  { primary: '#ec407a', secondary: '#111111' }, // pink / black
  { primary: '#ff7043', secondary: '#ffffff' }, // coral / white
];
/** Change strips used when the home colours clash. */
const AWAY_KITS: Kit[] = [
  { primary: '#ffffff', secondary: '#111111' },
  { primary: '#111111', secondary: '#f5c400' },
  { primary: '#ffeb3b', secondary: '#0b1f4b' },
  { primary: '#0b1f4b', secondary: '#9fd3ff' },
  { primary: '#00bfa5', secondary: '#0d1b2a' },
  { primary: '#ff6f00', secondary: '#0d1b2a' },
  { primary: '#d32f2f', secondary: '#ffffff' },
];
const KEEPER_COLORS = ['#39ff14', '#ff8c00', '#ffe600', '#8e24aa', '#00c2d1', '#ff4f9a', '#9aa0a6', '#222222', '#c6ff00'];

const COMPETITIONS = ['Regional League', 'National League', 'Premier Division', 'Championship', 'Continental Cup — Group B',
  'County Cup — Quarter-final', 'National Cup — Fifth Round', 'Continental Shield — Group D'];

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** "Redmean" perceptual-ish RGB distance, 0..~765. */
export function colorDistance(a: string, b: string): number {
  const [r1, g1, b1] = rgb(a), [r2, g2, b2] = rgb(b);
  const rm = (r1 + r2) / 2;
  const dr = r1 - r2, dg = g1 - g2, db = b1 - b2;
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}
const MIN_KIT_DISTANCE = 230;

// ---------------------------------------------------------------------------
// Generators
// ---------------------------------------------------------------------------

function weightedPick<T extends { w: number }>(rng: Rng, items: readonly T[]): T {
  let total = 0;
  for (const it of items) total += it.w;
  let r = rng.next() * total;
  for (const it of items) { r -= it.w; if (r <= 0) return it; }
  return items[items.length - 1]!;
}

function initials(name: string): string {
  const words = name.replace(/[^A-Za-z ]/g, '').split(' ').filter(Boolean);
  if (words.length === 1) return words[0]!.slice(0, 3).toUpperCase();
  if (words.length === 2) return (words[0]!.slice(0, 2) + words[1]![0]).toUpperCase();
  return words.map((w) => w[0]).join('').slice(0, 3).toUpperCase();
}

function clubName(rng: Rng, avoid?: string): { name: string; town: string } {
  for (let i = 0; i < 20; i++) {
    const town = rng.pick(TOWNS);
    if (avoid && avoid.includes(town)) continue;
    const name = rng.chance(0.12) ? `${rng.pick(PREFIX_STYLES)} ${town}` : `${town} ${rng.pick(SUFFIXES)}`;
    return { name, town };
  }
  return { name: 'Fallback Town', town: 'Fallback' };
}

export interface TeamOptions {
  /** Hex colour of the opponent's primary — the generated kit will contrast with it. */
  avoidColor?: string;
  /** Avoid re-using the opponent's town. */
  avoidName?: string;
  /** Force a team strength (approx average rating, 45..90). */
  strength?: number;
  formation?: TeamProfile['formation'];
  name?: string;
  /** Surnames already used by the opponent (keeps commentary unambiguous). */
  avoidSurnames?: string[];
}

export function generateTeam(rng: Rng, side: TeamSide, opts: TeamOptions = {}): TeamProfile {
  const { name: genName } = clubName(rng, opts.avoidName);
  const name = opts.name ?? genName;
  const shortName = initials(name.replace(/^(Sporting|Real|AFC|Dynamo|Inter) /, '').replace(/ (FC|AFC)$/, ''));

  // Kit: start from the club's own colours, fall back to a change strip on clash.
  let kit = rng.pick(KITS);
  if (opts.avoidColor && colorDistance(kit.primary, opts.avoidColor) < MIN_KIT_DISTANCE) {
    const pool = [...KITS, ...AWAY_KITS].filter((k) => colorDistance(k.primary, opts.avoidColor!) >= MIN_KIT_DISTANCE);
    kit = pool.length ? rng.pick(pool) : AWAY_KITS.reduce((best, k) =>
      colorDistance(k.primary, opts.avoidColor!) > colorDistance(best.primary, opts.avoidColor!) ? k : best);
  }
  const keeperPool = KEEPER_COLORS.filter((c) => colorDistance(c, kit.primary) > 200 && (!opts.avoidColor || colorDistance(c, opts.avoidColor) > 200));
  const keeperColor = keeperPool.length ? rng.pick(keeperPool) : '#39ff14';

  const formation = opts.formation ?? weightedPick(rng, [
    { f: '4-4-2' as const, w: 3 }, { f: '4-3-3' as const, w: 4 }, { f: '4-2-3-1' as const, w: 4 }, { f: '3-5-2' as const, w: 1.5 },
  ]).f;
  const strength = opts.strength ?? Math.round(rng.range(56, 82));
  const homeNation = rng.chance(0.85) ? 'ENG' : rng.pick(['SCO', 'IRL', 'WAL']);

  const usedNumbers = new Set<number>();
  const usedNames = new Set<string>(opts.avoidSurnames ?? []);
  let idx = 0;
  const makePlayer = (position: PlayerProfile['position'], preferredNum: number | null): PlayerProfile => {
    let number = preferredNum ?? 0;
    if (!number || usedNumbers.has(number)) {
      const pool = position === 'GK' ? [13, 25, 31, 12, 30, 40] : [12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 26, 27, 28, 29, 32, 33, 34, 35, 36, 37, 39, 44, 77, 99];
      number = pool.find((n) => !usedNumbers.has(n) && rng.chance(0.55)) ?? pool.find((n) => !usedNumbers.has(n)) ?? 50 + idx;
    }
    usedNumbers.add(number);
    let nationKey = rng.chance(0.55) ? homeNation : weightedPick(rng, DOMESTIC_MIX).key;
    let nation = NATIONS[nationKey]!;
    let first = '', last = '';
    for (let tries = 0; tries < 12; tries++) {
      first = rng.pick(nation.first); last = rng.pick(nation.last);
      if (!usedNames.has(last) && !usedNames.has(`${first} ${last}`)) break;
      nationKey = weightedPick(rng, DOMESTIC_MIX).key; nation = NATIONS[nationKey]!;
    }
    usedNames.add(last);
    const age = position === 'GK' ? rng.int(20, 37) : Math.round(Math.min(35, Math.max(17, rng.gaussian(26, 4))));
    const rating = Math.round(Math.min(99, Math.max(40, rng.gaussian(strength, 5) + (rng.chance(0.08) ? 7 : 0))));
    const appearances = Math.max(1, Math.round((age - 17) * rng.range(8, 30)));
    const goalRate = position === 'FWD' ? 0.42 : position === 'MID' ? 0.14 : position === 'DEF' ? 0.04 : 0;
    const seasonApps = rng.int(4, 30);
    const seasonGoals = Math.round(seasonApps * goalRate * rng.range(0.4, 1.5) * (rating / 70));
    const seasonAssists = Math.round(seasonApps * (position === 'GK' ? 0 : position === 'DEF' ? 0.06 : 0.17) * rng.range(0.3, 1.6));
    const id = `${side}-${number}`;
    idx++;
    return {
      id, number, name: `${first.charAt(0)}. ${last}`, shortName: last.toUpperCase(), position, rating,
      stats: { seasonGoals, seasonAssists, appearances, age, nationality: nation.name },
    };
  };

  const slots = FORMATIONS[formation];
  const players = slots.map((s) => makePlayer(s.pos, s.num));
  // Bench: GK, 2 DEF, 2 MID, 2 FWD
  const benchPos: PlayerProfile['position'][] = ['GK', 'DEF', 'DEF', 'MID', 'MID', 'FWD', 'FWD'];
  const bench = benchPos.map((p) => makePlayer(p, null)).map((p) => ({ ...p, rating: Math.max(40, p.rating - rng.int(0, 5)) }));

  const managerNation = NATIONS[rng.chance(0.7) ? homeNation : weightedPick(rng, DOMESTIC_MIX).key]!;
  let mNation = managerNation;
  let mLast = rng.pick(mNation.last);
  for (let i = 0; i < 12 && usedNames.has(mLast); i++) {
    mNation = NATIONS[weightedPick(rng, DOMESTIC_MIX).key]!;
    mLast = rng.pick(mNation.last);
  }
  const manager = `${rng.pick(mNation.first)} ${mLast}`;
  const form: ('W' | 'D' | 'L')[] = [];
  const pW = 0.2 + (strength - 56) / 26 * 0.4;
  for (let i = 0; i < 5; i++) {
    const r = rng.next();
    form.push(r < pW ? 'W' : r < pW + 0.28 ? 'D' : 'L');
  }
  const leaguePosition = Math.max(1, Math.min(20, Math.round(21 - (strength - 54) / 30 * 20 + rng.gaussian(0, 2.5))));

  return {
    id: `${side}-${shortName.toLowerCase()}`, side, name, shortName,
    primaryColor: kit.primary, secondaryColor: kit.secondary, keeperColor,
    formation, players, bench, manager, form, leaguePosition,
  };
}

export type MatchConfigOverrides = Partial<MatchConfig> & { homeOptions?: TeamOptions; awayOptions?: TeamOptions };

export function generateMatchConfig(seed: number, overrides: MatchConfigOverrides = {}): MatchConfig {
  const rng = createRng((seed ^ 0x7e4a11) >>> 0);
  const { homeOptions, awayOptions, ...rest } = overrides;
  const home = rest.home ?? generateTeam(rng, 'home', homeOptions);
  let away = rest.away;
  if (!away) {
    const homeSurnames = [...home.players, ...home.bench].map((p) => p.name.slice(p.name.indexOf(' ') + 1));
    away = generateTeam(rng, 'away', { avoidColor: home.primaryColor, avoidName: home.name.split(' ')[0], avoidSurnames: homeSurnames, ...awayOptions });
    if (away.keeperColor === home.keeperColor) away = { ...away, keeperColor: KEEPER_COLORS.find((c) => c !== home.keeperColor && colorDistance(c, home.primaryColor) > 200 && colorDistance(c, away!.primaryColor) > 200) ?? away.keeperColor };
  }
  const town = home.name.replace(/^(Sporting|Real|AFC|Dynamo|Inter) /, '').split(' ').filter((w) => !SUFFIXES.includes(w)).join(' ');
  const venue = rng.chance(0.3) ? `${rng.pick(GROUND_NAMES)}, ${town}` : `${town} ${rng.pick(GROUNDS)}`;
  const weather = weightedPick(rng, [
    { v: 'clear' as Weather, w: 36 }, { v: 'overcast' as Weather, w: 26 }, { v: 'rain' as Weather, w: 22 },
    { v: 'snow' as Weather, w: 7 }, { v: 'fog' as Weather, w: 9 },
  ]).v;
  const timeOfDay = weightedPick(rng, [
    { v: 'afternoon' as TimeOfDay, w: 40 }, { v: 'golden_hour' as TimeOfDay, w: 25 }, { v: 'night' as TimeOfDay, w: 35 },
  ]).v;
  const comp = rng.pick(COMPETITIONS);
  const competition = comp.includes('—') ? comp : `${comp} — Matchday ${rng.int(2, 38)}`;
  const drama = Math.round(Math.min(0.95, Math.max(0.2, rng.gaussian(0.55, 0.15))) * 100) / 100;
  return {
    seed, home, away, competition, venue, weather, timeOfDay,
    halfLengthSeconds: 240, drama,
    ...rest,
  };
}
